/**
 * Main task execution page orchestrator.
 * Handles: resolve → phone verify (when needed) → task view → actions.
 * Awareness links show a follow landing (native app only).
 */
import { useEffect, useState, useCallback, type ReactNode } from 'react';
import AppInstallHeadBanner from '@/components/AppInstallHeadBanner';
import { useAppInstallBanner } from '@/hooks/useAppInstallBanner';
import { finalizeWebAssigneeAfterVerify, rebindWebAcceptorAfterPhoneAuth, resolveTaskForWeb } from '../services/api';
import { classifyCallableError } from '../services/errors';
import { ensureSessionReady, hasVerifiedPhoneOnSession, signOutOfSession } from '@/services/auth';
import type { ResolveAwarenessResponse, ResolveTaskResponse, ResolveTaskResult } from '../types';
import { isAwarenessResolve, isExecuteResolve } from '../types';
import LoadingStep from '../steps/LoadingStep';
import PhoneVerifyStep from '../steps/PhoneVerifyStep';
import TaskReadyStep from '../steps/TaskReadyStep';
import TaskViewStep from '../steps/TaskViewStep';
import AwarenessLandingStep from '../steps/AwarenessLandingStep';
import ErrorScreen from '../components/ErrorScreen';

type Phase =
  | 'loading'
  | 'phone_verify'
  | 'awareness'
  | 'task_ready'
  | 'task_view'
  | 'error';

interface Props {
  shortId: string;
  /**
   * Skip the ready gate and land on the task itself (D18).
   *
   * `TaskReadyStep` orients someone arriving cold from a shared link — it shows
   * which task this is and offers the native app. A reader who tapped the row
   * in their own list has already seen the title and status and chosen it, so
   * for them the gate is a second tap that says nothing new.
   */
  directOpen?: boolean;
}

export default function TaskPage({ shortId, directOpen = false }: Props) {
  const installBanner = useAppInstallBanner({ softShowOnMount: true });
  const [phase, setPhase] = useState<Phase>('loading');
  const [loadingLabel, setLoadingLabel] = useState<string>('Validating task…');
  const [taskData, setTaskData] = useState<ResolveTaskResponse | null>(null);
  const [awarenessData, setAwarenessData] = useState<ResolveAwarenessResponse | null>(null);
  const [errorReason, setErrorReason] = useState<string>('');
  const [forcePhoneInput, setForcePhoneInput] = useState(false);

  const resolve = useCallback(async (label?: string) => {
    setPhase('loading');
    if (label) setLoadingLabel(label);
    try {
      await ensureSessionReady();

      let result: ResolveTaskResult = await resolveTaskForWeb(shortId);

      if (!result.ok && result.reason === 'auth_required') {
        if (await hasVerifiedPhoneOnSession()) {
          result = await resolveTaskForWeb(shortId);
        }
      }

      if (isAwarenessResolve(result)) {
        setAwarenessData(result);
        setPhase('awareness');
        return;
      }

      if (
        isExecuteResolve(result)
        && result.task.executionAvailability === 'claimed_by_other'
        && result.task.viewerRole !== 'owner'
      ) {
        try {
          const rebind = await rebindWebAcceptorAfterPhoneAuth(shortId);
          if (rebind.ok && rebind.rebound) {
            result = await resolveTaskForWeb(shortId);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('Failed to recover web acceptor session:', err);
        }
      }

      if (isExecuteResolve(result)) {
        if (result.task.assigneeVerificationRequired) {
          try {
            await finalizeWebAssigneeAfterVerify(shortId);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to finalize assignee after verify:', err);
          }
          result = await resolveTaskForWeb(shortId);
        }

        if (isExecuteResolve(result)) {
          setTaskData(result);
          setPhase(directOpen ? 'task_view' : 'task_ready');
        } else if (isAwarenessResolve(result)) {
          setAwarenessData(result);
          setPhase('awareness');
        } else {
          setErrorReason(result.ok ? 'network_error' : result.reason);
          setPhase('error');
        }
      } else if (!result.ok && result.reason === 'auth_required') {
        setPhase('phone_verify');
      } else {
        setErrorReason(result.ok ? 'network_error' : result.reason);
        setPhase('error');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to resolve task:', err);
      setErrorReason(classifyCallableError(err));
      setPhase('error');
    }
  }, [shortId, directOpen]);

  useEffect(() => {
    resolve('Validating task…');
  }, [resolve]);

  const handlePhoneVerified = useCallback(async (meta?: { priorAnonymousUid?: string }) => {
    if (meta?.priorAnonymousUid) {
      try {
        await rebindWebAcceptorAfterPhoneAuth(shortId, meta.priorAnonymousUid);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Failed to rebind web acceptor after phone verify:', err);
      }
    }
    setForcePhoneInput(false);
    resolve('Loading task…');
  }, [resolve, shortId]);

  const handleAssigneeVerificationRequired = useCallback(async () => {
    if (await hasVerifiedPhoneOnSession()) {
      try {
        await finalizeWebAssigneeAfterVerify(shortId);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to finalize assignee after accept:', err);
      }
      resolve('Saving your assignment…');
      return;
    }
    setPhase('phone_verify');
  }, [resolve, shortId]);

  const handleTaskUpdated = useCallback(() => {
    resolve('Refreshing…');
  }, [resolve]);

  const handleChangePhone = useCallback(async () => {
    setPhase('loading');
    try {
      await signOutOfSession();
    } catch {
      // Still collect a new number — the stale session is what blocked us.
    }
    setForcePhoneInput(true);
    setPhase('phone_verify');
  }, []);

  const withInstallBanner = (node: ReactNode) => (
    <>
      <AppInstallHeadBanner
        open={installBanner.visible}
        onDismiss={installBanner.dismiss}
      />
      {node}
    </>
  );

  switch (phase) {
    case 'loading':
      return withInstallBanner(<LoadingStep label={loadingLabel} />);
    case 'awareness':
      // AwarenessLandingStep owns its own banner (failed Open in app).
      return awarenessData ? (
        <AwarenessLandingStep
          title={awarenessData.preview.title}
          status={awarenessData.preview.status}
          deepLink={awarenessData.deepLink}
        />
      ) : null;
    case 'task_ready':
      return taskData ? withInstallBanner(
        <TaskReadyStep
          task={taskData.task}
          onViewTask={() => setPhase('task_view')}
          installBanner={installBanner}
        />,
      ) : null;
    case 'phone_verify':
      return withInstallBanner(
        <PhoneVerifyStep
          forceInput={forcePhoneInput}
          onVerified={handlePhoneVerified}
        />,
      );
    case 'task_view':
      return taskData ? withInstallBanner(
        <TaskViewStep
          shortId={shortId}
          task={taskData.task}
          onTaskUpdated={handleTaskUpdated}
          onAssigneeVerificationRequired={handleAssigneeVerificationRequired}
        />,
      ) : null;
    case 'error':
      return withInstallBanner(
        <ErrorScreen
          reason={errorReason}
          onRetry={() => resolve('Validating task…')}
          onChangePhone={errorReason === 'phone_mismatch' ? () => void handleChangePhone() : undefined}
        />,
      );
  }
}
